import React, { type ReactNode, type RefObject } from 'react';
import { DragHandleProps, DraggableProvided } from 'react-beautiful-dnd'; // If you're using `react-beautiful-dnd`
import cx from 'classnames';

import { Button } from '@plone/components';
import includes from 'lodash/includes';
import isBoolean from 'lodash/isBoolean';
import { defineMessages, injectIntl, type IntlShape } from 'react-intl';
import doesNodeContainClick from '@redturtle/qubicaamf-theme/components/Blocks/Tabs/doesNodeContainClick';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import BlockChooser from '@plone/volto/components/manage/BlockChooser/BlockChooser';
import addSVG from '@plone/volto/icons/circle-plus.svg';
import trashSVG from '@plone/volto/icons/delete.svg';
import type { BlockEditProps } from '@plone/types';
import type { QubicaBlocksConfig } from '@redturtle/qubicaamf-theme/config/blocks';
import config from '@plone/registry';

const messages = defineMessages({
  unknownBlock: {
    id: 'Unknown Block',
    defaultMessage: 'Unknown Block {block}',
  },
  delete: {
    id: 'delete',
    defaultMessage: 'delete',
  },
});

type BlockProps = Omit<BlockEditProps, 'blocksConfig'> & {
  blocksConfig: QubicaBlocksConfig;
};

type EditBlockWrapperProps = {
  intl: IntlShape;
  blockProps: BlockProps;
  extraControls?: ReactNode;
  disabled?: boolean;
  multiSelected?: boolean;
  children?: ReactNode;
  draginfo?: {
    innerRef: RefObject<HTMLDivElement>;
    draggableProps: DraggableProvided['draggableProps'];
    dragHandleProps: DragHandleProps;
  };
};

type EditBlockWrapperState = {
  addNewBlockOpened: boolean;
};

class EditBlockWrapper extends React.Component<
  EditBlockWrapperProps,
  EditBlockWrapperState
> {
  constructor(props: EditBlockWrapperProps) {
    super(props);
    this.state = {
      addNewBlockOpened: false,
    };
    this.blockNode = React.createRef<HTMLDivElement>();
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside, false);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = (e: MouseEvent) => {
    if (
      this.blockNode.current &&
      doesNodeContainClick(this.blockNode.current, e)
    ) {
      return;
    }

    if (this.state.addNewBlockOpened) {
      this.setState({
        addNewBlockOpened: false,
      });
      return true;
    }
  };
  blockNode: RefObject<HTMLDivElement>;

  render() {
    // const { intl, blockProps, extraControls, disabled, children } =
    const { intl, blockProps, extraControls, disabled, children, draginfo } =
      this.props;

    const {
      allowedBlocks,
      block,
      data,
      onDeleteBlock,
      onInsertBlock,
      onMutateBlock,
      selected,
    } = blockProps;
    const type = data['@type'];
    const { disableNewBlocks } = data;
    const visible = selected;

    const required = isBoolean(data.required)
      ? data.required
      : includes(config.blocks.requiredBlocks, type);

    const allowedBlocksFromConfig = blockProps.blocksConfig.tabs?.allowedBlocks;

    return (
      <div ref={this.blockNode}>
        <div
          ref={draginfo?.innerRef}
          {...(selected ? draginfo?.draggableProps : null)}
          className={`block-editor-${data['@type']}`}
        >
          {(!selected || !visible || disabled) && (
            <div
              style={{
                display: 'none',
                // keep react-beautiful-dnd happy
              }}
              {...draginfo?.dragHandleProps}
            ></div>
          )}
          {visible && (
            <div className="block-toolbar">
              {extraControls}

              {!disabled && (
                <>
                  {!disableNewBlocks && (
                    <Button
                      aria-label="Add block"
                      onPress={() => {
                        this.setState({
                          addNewBlockOpened: !this.state.addNewBlockOpened,
                        });
                      }}
                      className="tabs-block-add-button"
                    >
                      <Icon name={addSVG} className="" size="19px" />
                    </Button>
                  )}
                  {!required && (
                    <Button
                      onPress={() => onDeleteBlock(block, true)}
                      className="delete-button-tabs-block"
                      aria-label={intl.formatMessage(messages.delete)}
                    >
                      <Icon name={trashSVG} size="19px" color="#ff004d" />
                    </Button>
                  )}
                  {this.state.addNewBlockOpened && (
                    <BlockChooser
                      // @ts-ignore
                      onMutateBlock={(id, value) => {
                        onMutateBlock(id, value);
                        this.setState({ addNewBlockOpened: false });
                      }}
                      onInsertBlock={(id, value) => {
                        onInsertBlock(id, value, block);
                        this.setState({ addNewBlockOpened: false });
                      }}
                      currentBlock={block}
                      allowedBlocks={allowedBlocks || allowedBlocksFromConfig}
                    />
                  )}
                </>
              )}
            </div>
          )}

          <div
            className={cx('ui drag block wrapper inner', type, {
              multiSelected: this.props.multiSelected,
            })}
          >
            {children}
          </div>
        </div>
      </div>
    );
  }
}

export default injectIntl(EditBlockWrapper);
